//
// social-index/renderers
//

import loc from '../../../core/js/localise';
import create_element from '../../../core/js/helpers/create-element';
import remove_node from '../../../core/js/helpers/remove-node';
import * as collection_classlist from '../../../core/js/helpers/collection-classlist';

const helpers = require('./helpers');


// index_selector_list_renderer - renders the si selector
// ----------------------------
// Responds to:
//    - si/set-group - set select option value; display the current selector list of indexes
//    - si/set-index - apply 'selected' state to matching index pill
//
// Hooks:
//   - js-si-selector:            wrapper for the entire selector area
//   - js-si-selector-list:       wrapper for the pills list - the IMMEDIATE parent of pills
//                                (nodes are inserted into this element)
//   - js-si-selector-list-item:  pill items
//   - js-si-selector-list-item-container: immediate parent of js-si-selector-list-item
//

const index_selector_list_renderer = (function() {
  const el_selector_wrap = document.querySelector('.js-si-selector');
  const e = {
    group_select: el_selector_wrap.getElementsByTagName('select')[0],
    index_list:   el_selector_wrap.querySelector('.js-si-selector-list'),
  };

  let cur_group_name;

  function init() {
    window.addEventListener('si/set-group', cb_set_group);
    window.addEventListener('si/set-index', cb_set_index);
  }
  init();

  function cb_set_group(ev) {
    const group_name = ev.detail.group_name;
    if (group_name === cur_group_name) {
      return;
    }

    const group = helpers.find_group(group_name);
    if (!group) {
      return;
    }

    cur_group_name = group_name;

    e.group_select.value = group_name;
    set_index_pills(group['group-items']);
  }

  function cb_set_index(ev) {
    collection_classlist.remove(cur_index_pills(), 's-selected');

    const pill = find_index_pill(ev.detail.index_name);
    if (pill) {
      pill.classList.add('s-selected');
    }
  }

  function set_index_pills(indexes) {
    clear_index_pills();

    const pills = indexes.map(function(si, i) {
      const el = create_element(`
        <a class="index-controls__pills__item-pos js-si-selector-list-item-container" style="animation-delay: ${i/20}s;"
           data-ga="interaction" data-ga-action="click-control" data-ga-label="${si.name}">
          <span class="index-controls__pills__item js-si-selector-list-item">
            ${si.name}
          </span>
        </a>
      `);

      e.index_list.insertBefore(el, null);
      return el;
    });
  }

  function clear_index_pills() {
    const index_btns = cur_index_pills();
    if (index_btns.length) {
      index_btns.forEach(remove_node);
    }
  }

  function cur_index_pills() {
    return Array.from(e.index_list.getElementsByClassName('js-si-selector-list-item-container'));
  }

  function find_index_pill(index_name) {
    return cur_index_pills().find(el => el.textContent.trim() === index_name);
  }
})();



// si_index_description_renderer
// ----------------------------
// Responds to:
//    - si/set-index   - updates the current si title & description
//
// Hooks:
//    - js-c-si-description                 wrapper for the title block
//    - js-c-si-description-title           |
//    - js-c-si-description-time-period     |elements whose textContents is to be updated
//    - js-c-si-description-text            |
//

const si_index_description_renderer = (function() {
  const el_descr_wrap = document.querySelector('.js-c-si-description');
  const e = {
    index_name:        el_descr_wrap.querySelector('.js-c-si-description-title'),
    index_time_period: el_descr_wrap.querySelector('.js-c-si-description-time-period'),
    index_descr_text:  el_descr_wrap.querySelector('.js-c-si-description-text'),
  };

  function init() {
    window.addEventListener('si/set-index', cb_set);
  }
  init();

  function cb_set(ev) {
    const name = ev.detail.index_name;
    const description = helpers.find_index_prop(name, 'description');
    const period =
      helpers.find_index_prop(name, 'time-period') ||
      window.social_index_default_time_period;

    update_index_name(name);
    update_index_time_period(period);
    update_index_descr(description);
  }

  function update_index_name(index_name) {
    e.index_name.textContent = index_name;
  }

  function update_index_time_period(time_period) {
    e.index_time_period.textContent = time_period;
  }

  function update_index_descr(description) {
    e.index_descr_text.textContent = description;
  }
})();



// si_table_renderer - main index (table) renderer
// ----------------------------
// Responds to:
//    - si/load-begin   - updates table visual state
//    - si/load-finish  - updates table visual state
//    - si/load-error   - updates table visual state
//
// Sends:
//    - si/render-finish
//
// Hooks:
//    - js-c-si-table:  wrapper for both table header & body
//    - js-c-si-list:   the table body
//    - js-c-si-row-header:  the header table row
//    - js-c-si-row:         a table row from the main table body
//    - js-si-load-error:  load-error-message container
//    - js-si-col-data:         a column element.
//                              the textContent of this element must be the data value
//    - js-si-col-data--total:  the 'total' column element

const si_table_renderer = (function() {
  const el_wrap = document.querySelector('.js-si-table');
  const e = {
    wrap:       el_wrap,
    list:       document.querySelector('.js-c-si-list'),
    header_row: el_wrap.querySelector('.js-c-si-row-header'),
  };

  function init() {
    window.addEventListener('si/load-begin', cb_begin_load);
    window.addEventListener('si/load-finish', cb_finish_load);
    window.addEventListener('si/load-error', cb_error);
    window.addEventListener('si/sort', cb_sort);
  }
  init();

  function disp__hide() {
    const el_error = document.querySelector('.js-si-load-error');
    if (el_error) {
      remove_node(el_error);
    }
  }

  function disp__remove_current() {
    cur_items().forEach(remove_node);
  }

  function disp__insert(items) {
    disp__remove_current();

    // CSS animation plays automatically when adding nodes to dom
    items.forEach(function(item, i) {
      item.setAttribute('style', `animation-delay: ${i / 60}s;`);
      e.list.appendChild(item)
    });

    window.dispatchEvent(new CustomEvent('si/render-finish'));
  }

  function cb_begin_load(ev) {
    disp__hide();
  }

  function cb_finish_load(ev) {
    const index_data = ev.detail;
    let items = index_data.map(create_row_el);
    items = sort_items_by_field(items, null, helpers.SORT_DESC);

    disp__insert(items);
  }

  function cur_items() {
    return Array.from(e.list.getElementsByClassName('js-c-si-row'));
  }

  function sort_items_by_field(_rows, i_field, direction) {
    let rows = _rows.slice();

    rows.sort(function(a, b) {
      const cols_a = a.getElementsByClassName('js-si-data-col');
      const cols_b = b.getElementsByClassName('js-si-data-col');

      const i_sortcol = i_field === null ? cols_a.length-1 : i_field;

      const val_a = parseFloat(cols_a[i_sortcol].innerText);
      const val_b = parseFloat(cols_b[i_sortcol].innerText);

      return direction === helpers.SORT_DESC ? val_b - val_a : val_a - val_b;
    });

    return rows;
  }

  function cb_sort(ev) {
    const items = cur_items();
    if (items.length === 0) {
      return;
    }

    const reordered_list = sort_items_by_field(items, ev.detail.field_index, ev.detail.direction);

    disp__hide();
    disp__insert(reordered_list);
  }

  function cb_error(ev) {
    const txt = loc('item_load_error');

    e.list.innerHTML = `
      <div class="index-load-error js-si-load-error">
        <h3 class="c-heading c-heading--2 c-heading--bottom-close">
          ${txt[0].replace('__INDEX_NAME__', ev.detail.index_name)}
        </h3>
        <p class="c-copy c-copy--2 measure-wide mx-auto">
          ${txt[1]}
        </p>
      </div>
    `;
  }

  function create_row_el(row) {
    const company_name = row[0].trim();
    const data = row.slice(1);

    // find logo
    const logo = (function() {
      const logos = window.social_index_logos || [ ];
      const logo = logos.find(l => helpers.name_matches(l['company-name'], company_name));

      return logo ? logo['logo'] : null;
    })();

    const logo_snippet = !logo ? '' : `
      <div class="index-table__logo">
        <img class="index-table__logo__img" src="${logo}">
      </div>
    `;
    const has_logo = !logo ? '' : `
      index-table__col--has-logo
    `;

    return create_element(`
      <li class="index-table__row index-table__row--body js-c-si-row">
        <div class="index-table__row__pos">
          <div class="index-table__col index-table__col--title ${has_logo}">
            ${logo_snippet}
            ${company_name}
          </div>
          ${data.map(function(item, i) {
            const mods = i === data.length-1 ? 'js-si-data-col--total index-table__col--total' : 'index-table__col--std';

            return `<div class="index-table__col js-si-data-col ${mods}">${item}</div>`;
          }).join('')}
        </div>
      </li>
    `);
  }
})();



// si_report_renderer
// ----------------------------
// Responds to:
//    - si/set-index - updates the displayed content item
//

const si_report_renderer = (function() {
  const el_block = document.querySelector('.c-si-report-block');
  const e = {
    heading:   el_block.querySelector('.c-heading'),
    text:      el_block.querySelector('.c-content'),
    button:    el_block.querySelector('.c-btn'),
    thumbnail: el_block.querySelector('.js-csrg-graphic-thumbnail'),
  };

  function init() {
    window.addEventListener('si/set-index', cb_set);
  }
  init();

  function set_highlight_color(col) {
    const fills = Array.from(el_block.querySelectorAll('[fill]'));
    fills.forEach(item => item.setAttribute('fill', col));
  }

  function cb_set(ev) {
    const index_name = ev.detail.index_name;
    const item = helpers.find_index_prop(index_name, 'content-item');

    if (!item) {
      el_block.style.display = 'none';
      return;
    }

    el_block.style.display = 'block';

    e.heading.innerHTML = helpers.find_index_prop(index_name, 'content-header');
    e.text.innerHTML    = helpers.find_index_prop(index_name, 'content-text');
    e.button.href       = helpers.find_index_prop(index_name, 'content-url');
    if (e.thumbnail) {
      e.thumbnail.src = helpers.find_index_prop(index_name, 'content-thumb-url');
    }
    set_highlight_color(helpers.find_index_prop(index_name, 'content-highlight-col'));
  }
})();

